import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from '@/hooks/useUserRole';
import { useApp } from '@/contexts/AppContext';
import { useContentHealth, getCardDueInfo, type ContentCardFull, type SectionHealth } from '@/hooks/useContentHealth';
import { ArrowLeft, Check, Copy, ClipboardCheck, Pencil, AlertTriangle, Plus, MessageSquare } from 'lucide-react';
import { format, parseISO, addMonths, differenceInDays, subDays } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { Playbook } from '@/components/playbooks/PlaybookList';
import {
  AppButton,
  AppDisclosureGlyph,
  AppIconButton,
  AppNativeCheckbox,
  AppNativeSelect,
  AppPanelButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppHeaderBar,
  AppLoadingState,
  AppPage,
  AppSectionHeader,
  AppText,
  AppTextArea,
  AppWebInput,
} from '../../ui-foundation';
import {
  AppDialog as Dialog,
  AppDialogContent as DialogContent,
  AppDialogHeader as DialogHeader,
  AppDialogTitle as DialogTitle,
} from '../../ui-foundation/adapters/web';

type ReviewTarget = {
  contentType: 'content_card' | 'playbook';
  contentId: string;
  title: string;
};

type ContentReviewEntry = {
  id: string;
  content_type: 'content_card' | 'playbook';
  content_id: string;
  note: string;
  review_outcome: 'note' | 'reviewed' | 'updated';
  created_at: string;
  created_by: string;
};

export default function AdminContentPage() {
  const navigate = useNavigate();
  const { user } = useApp();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const { sections, cards, totalDue, loading, refetch } = useContentHealth();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [promptCard, setPromptCard] = useState<ContentCardFull | null>(null);
  const [bulkPromptOpen, setBulkPromptOpen] = useState(false);
  const [editCard, setEditCard] = useState<ContentCardFull | null>(null);

  // Playbooks state
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [playbooksLoading, setPlaybooksLoading] = useState(true);
  const [playbooksExpanded, setPlaybooksExpanded] = useState(false);
  const [editPlaybook, setEditPlaybook] = useState<Playbook | null>(null);
  const [addPlaybook, setAddPlaybook] = useState(false);
  const [promptPlaybook, setPromptPlaybook] = useState<Playbook | null>(null);
  const [reviewTarget, setReviewTarget] = useState<ReviewTarget | null>(null);
  const [reviewEntries, setReviewEntries] = useState<ContentReviewEntry[]>([]);
  const [reviewEntriesLoading, setReviewEntriesLoading] = useState(false);
  const [reviewDraft, setReviewDraft] = useState('');
  const [savingReviewDraft, setSavingReviewDraft] = useState(false);
  const [recentReviewEntries, setRecentReviewEntries] = useState<ContentReviewEntry[]>([]);
  const [recentReviewLoading, setRecentReviewLoading] = useState(true);

  useEffect(() => {
    if (!roleLoading && !isAdmin) navigate('/money', { replace: true });
  }, [roleLoading, isAdmin, navigate]);

  const fetchPlaybooks = async () => {
    const { data } = await supabase.from('playbooks').select('*').order('section').order('sort_order');
    setPlaybooks((data as any as Playbook[]) || []);
    setPlaybooksLoading(false);
  };

  useEffect(() => { fetchPlaybooks(); }, []);

  const loadRecentReviewEntries = async () => {
    setRecentReviewLoading(true);
    const { data, error } = await supabase
      .from('content_review_entries' as any)
      .select('*')
      .order('created_at', { ascending: false })
      .limit(12);

    if (error) {
      console.error(error);
      setRecentReviewEntries([]);
    } else {
      setRecentReviewEntries((data || []) as ContentReviewEntry[]);
    }

    setRecentReviewLoading(false);
  };

  useEffect(() => {
    if (!isAdmin) return;
    void loadRecentReviewEntries();
  }, [isAdmin]);

  const loadReviewEntries = async (target: ReviewTarget) => {
    setReviewTarget(target);
    setReviewDraft('');
    setReviewEntriesLoading(true);

    const { data, error } = await supabase
      .from('content_review_entries' as any)
      .select('*')
      .eq('content_type', target.contentType)
      .eq('content_id', target.contentId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error(error);
      toast.error('Could not load the review history.');
      setReviewEntries([]);
    } else {
      setReviewEntries((data || []) as ContentReviewEntry[]);
    }

    setReviewEntriesLoading(false);
  };

  const addReviewEntry = async (target: ReviewTarget, note: string, reviewOutcome: ContentReviewEntry['review_outcome']) => {
    if (!user) return false;

    const trimmedNote = note.trim();
    if (!trimmedNote) return false;

    const { error } = await supabase.from('content_review_entries' as any).insert({
      content_type: target.contentType,
      content_id: target.contentId,
      note: trimmedNote,
      review_outcome: reviewOutcome,
      created_by: user.id,
    });

    if (error) {
      console.error(error);
      toast.error('Could not save that review note.');
      return false;
    }

    if (reviewTarget?.contentId === target.contentId && reviewTarget.contentType === target.contentType) {
      await loadReviewEntries(target);
    }

    await loadRecentReviewEntries();

    return true;
  };

  const saveReviewDraft = async () => {
    if (!reviewTarget || !reviewDraft.trim()) return;
    setSavingReviewDraft(true);
    const saved = await addReviewEntry(reviewTarget, reviewDraft, 'note');
    if (saved) {
      setReviewDraft('');
      toast.success('Review note saved.');
    }
    setSavingReviewDraft(false);
  };

  const markReviewed = async (id: string) => {
    await supabase.from('content_cards').update({ last_reviewed_at: format(new Date(), 'yyyy-MM-dd'), updated_at: new Date().toISOString() } as any).eq('id', id);
    await addReviewEntry({ contentType: 'content_card', contentId: id, title: 'Content card' }, 'Marked reviewed for accuracy.', 'reviewed');
    toast.success('Marked as reviewed');
    refetch();
  };

  const markPlaybookReviewed = async (id: string) => {
    await supabase.from('playbooks').update({ last_reviewed_at: format(new Date(), 'yyyy-MM-dd'), updated_at: new Date().toISOString() } as any).eq('id', id);
    await addReviewEntry({ contentType: 'playbook', contentId: id, title: 'Guide' }, 'Marked reviewed for accuracy.', 'reviewed');
    toast.success('Marked as reviewed');
    fetchPlaybooks();
  };

  if (roleLoading || loading) {
    return (
      <AppPage className="min-h-screen items-center justify-center">
        <AppLoadingState
          title="Loading content health..."
          body="Fetching cards, guides, and recent review activity."
        />
      </AppPage>
    );
  }

  const dueCards = cards.filter(c => {
    const { isOverdue, isDueSoon } = getCardDueInfo(c);
    return isOverdue || isDueSoon;
  });
  const duePlaybooks = playbooks.filter((playbook) => {
    const dueDate = addMonths(parseISO(playbook.last_reviewed_at), playbook.review_frequency_months);
    return differenceInDays(dueDate, new Date()) <= 30;
  });
  const recentReviewWindowStart = subDays(new Date(), 30).getTime();
  const recentReviewStats = recentReviewEntries.reduce(
    (accumulator, entry) => {
      if (new Date(entry.created_at).getTime() < recentReviewWindowStart) return accumulator;
      accumulator.total += 1;
      if (entry.review_outcome === 'reviewed') accumulator.reviewed += 1;
      if (entry.review_outcome === 'updated') accumulator.updated += 1;
      if (entry.review_outcome === 'note') accumulator.notes += 1;
      return accumulator;
    },
    { total: 0, reviewed: 0, updated: 0, notes: 0 },
  );
  const cardTitleLookup = new Map(cards.map((card) => [card.id, card.title]));
  const playbookTitleLookup = new Map(playbooks.map((playbook) => [playbook.id, playbook.title]));

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <AppHeaderBar
          title="Content Health"
          topPadding="$md"
          bottomPadding="$sm"
          action={
            <AppButton tone="ghost" size="sm" onPress={() => navigate('/admin')}>
              <ArrowLeft size={16} />
              Back
            </AppButton>
          }
        />
      </div>

      <AppPage className="max-w-lg space-y-4 animate-fade-in">
        <AppCard>
          <AppCardHeader>
            <AppSectionHeader
              title="Review summary"
              subtitle={`${cards.length} content cards and ${playbooks.length} guides across ${sections.length} section${sections.length !== 1 ? 's' : ''}.`}
            />
          </AppCardHeader>
          <AppCardBody>
          {totalDue > 0 ? (
            <AppText className="mt-1 text-xs text-amber-600">⚠️ {totalDue} card{totalDue !== 1 ? 's' : ''} need{totalDue === 1 ? 's' : ''} reviewing</AppText>
          ) : (
            <AppText className="mt-1 text-xs text-green-600">✅ All content is up to date</AppText>
          )}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="rounded-xl border border-border bg-background px-3 py-3">
              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Due now</p>
              <p className="text-lg font-bold text-foreground mt-1">{totalDue + duePlaybooks.length}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Cards and guides needing review in the next 30 days.</p>
            </div>
            <div className="rounded-xl border border-border bg-background px-3 py-3">
              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Last 30 days</p>
              <p className="text-lg font-bold text-foreground mt-1">{recentReviewStats.total}</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                {recentReviewStats.reviewed} reviewed · {recentReviewStats.updated} updated · {recentReviewStats.notes} notes
              </p>
            </div>
          </div>
          </AppCardBody>
        </AppCard>

        {dueCards.length > 0 && (
          <AppButton onPress={() => setBulkPromptOpen(true)} width="100%">
            Review all due ({dueCards.length} cards) — Get AI prompt
          </AppButton>
        )}

        {/* Content card sections */}
        {sections.map(section => (
          <SectionCard key={section.section} section={section} isExpanded={expandedSection === section.section}
            onToggle={() => setExpandedSection(expandedSection === section.section ? null : section.section)}
            onMarkReviewed={markReviewed}
            onGetPrompt={setPromptCard}
            onEdit={setEditCard}
            onOpenReview={(card) => loadReviewEntries({ contentType: 'content_card', contentId: card.id, title: card.title })}
          />
        ))}

        {/* Playbooks section */}
        <AppCard>
          <AppCardHeader>
            <AppSectionHeader
              title="My Guides"
              subtitle="Review guide freshness, update details, and add new guides."
              action={
                <AppButton tone="ghost" size="sm" onPress={() => setAddPlaybook(true)}>
                  <Plus size={14} />
                  Add guide
                </AppButton>
              }
            />
          </AppCardHeader>
          <AppCardBody>

          {playbooksLoading ? (
            <AppText fontSize="$sm" color="$colorMuted">Loading…</AppText>
          ) : (
            <div className="space-y-2">
              {playbooks.map(pb => {
                const dueDate = addMonths(parseISO(pb.last_reviewed_at), pb.review_frequency_months);
                const daysUntilDue = differenceInDays(dueDate, new Date());
                const isOverdue = daysUntilDue < 0;
                const isDueSoon = !isOverdue && daysUntilDue <= 30;

                return (
                  <div key={pb.id} className="bg-card rounded-xl border border-border p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{pb.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{pb.title}</p>
                        <p className={cn('text-[10px]', isOverdue ? 'text-destructive' : isDueSoon ? 'text-amber-600' : 'text-muted-foreground')}>
                          {pb.section} · {isOverdue ? `${Math.abs(daysUntilDue)}d overdue` : isDueSoon ? `Due in ${daysUntilDue}d` : `OK (${daysUntilDue}d)`}
                          {' · '}{pb.is_active ? '✅ Active' : '⏸️ Inactive'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {(isOverdue || isDueSoon) && (
                          <AppIconButton onClick={() => setPromptPlaybook(pb)} title="Get AI prompt">
                            <AlertTriangle size={14} className="text-amber-500" />
                          </AppIconButton>
                        )}
                        <AppIconButton
                          onClick={() => loadReviewEntries({ contentType: 'playbook', contentId: pb.id, title: pb.title })}
                          title="Review notes"
                        >
                          <MessageSquare size={14} className="text-muted-foreground" />
                        </AppIconButton>
                        <AppIconButton onClick={() => setEditPlaybook(pb)} title="Edit">
                          <Pencil size={14} className="text-muted-foreground" />
                        </AppIconButton>
                        <AppIconButton onClick={() => markPlaybookReviewed(pb.id)} title="Mark reviewed">
                          <Check size={14} className="text-primary" />
                        </AppIconButton>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </AppCardBody>
        </AppCard>

        <AppCard>
          <AppCardHeader>
            <AppSectionHeader
              title="Recent review activity"
              subtitle="Keep review decisions, updates, and follow-up notes visible so content work stays disciplined instead of ad hoc."
            />
          </AppCardHeader>
          <AppCardBody>

          {recentReviewLoading ? (
            <AppText fontSize="$sm" color="$colorMuted">Loading review activity…</AppText>
          ) : recentReviewEntries.length === 0 ? (
            <AppText fontSize="$sm" color="$colorMuted">No review activity logged yet.</AppText>
          ) : (
            <div className="space-y-2">
              {recentReviewEntries.slice(0, 6).map((entry) => {
                const title = entry.content_type === 'content_card'
                  ? cardTitleLookup.get(entry.content_id) || 'Content card'
                  : playbookTitleLookup.get(entry.content_id) || 'Guide';
                const outcomeLabel = entry.review_outcome === 'reviewed'
                  ? 'Reviewed'
                  : entry.review_outcome === 'updated'
                    ? 'Updated'
                    : 'Note';

                return (
                  <div key={entry.id} className="rounded-xl border border-border bg-background px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-foreground">{title}</p>
                      <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                        {outcomeLabel}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {format(new Date(entry.created_at), 'd MMM yyyy, HH:mm')}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">
                      {entry.note}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
          </AppCardBody>
        </AppCard>
      </AppPage>

      <PromptDialog card={promptCard} onClose={() => setPromptCard(null)} />
      <BulkPromptDialog cards={dueCards} open={bulkPromptOpen} onClose={() => setBulkPromptOpen(false)} />
      {editCard && <EditCardDialog card={editCard} onClose={() => setEditCard(null)} onSaved={() => { setEditCard(null); refetch(); }} />}
      {promptPlaybook && <PlaybookPromptDialog playbook={promptPlaybook} onClose={() => setPromptPlaybook(null)} />}
      {reviewTarget && (
        <ReviewHistoryDialog
          target={reviewTarget}
          entries={reviewEntries}
          loading={reviewEntriesLoading}
          draft={reviewDraft}
          saving={savingReviewDraft}
          onDraftChange={setReviewDraft}
          onSave={saveReviewDraft}
          onClose={() => setReviewTarget(null)}
        />
      )}
      {(editPlaybook || addPlaybook) && (
        <EditPlaybookDialog
          playbook={editPlaybook}
          onClose={() => { setEditPlaybook(null); setAddPlaybook(false); }}
          onSaved={() => { setEditPlaybook(null); setAddPlaybook(false); fetchPlaybooks(); }}
        />
      )}
    </div>
  );
}

// === Content card sub-components (unchanged) ===

function SectionCard({ section, isExpanded, onToggle, onMarkReviewed, onGetPrompt, onEdit, onOpenReview }: {
  section: SectionHealth; isExpanded: boolean; onToggle: () => void;
  onMarkReviewed: (id: string) => void; onGetPrompt: (card: ContentCardFull) => void; onEdit: (card: ContentCardFull) => void;
  onOpenReview?: (card: ContentCardFull) => void;
}) {
  const statusIcon = section.status === 'green' ? '🟢' : section.status === 'yellow' ? '🟡' : '🔴';
  const statusText = section.status === 'green' ? 'All up to date' : section.status === 'yellow' ? 'Review due' : 'Overdue';
  const needsAttention = [...section.overdue, ...section.dueSoon];

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <AppPanelButton onClick={onToggle} className="gap-3 rounded-none border-0 p-4 active:bg-muted/50">
        <span className="text-xl">{section.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">{section.label}</p>
          <p className="text-xs text-muted-foreground">{section.total} cards · {statusIcon} {statusText}</p>
        </div>
        <AppDisclosureGlyph open={isExpanded} />
      </AppPanelButton>
      {isExpanded && (
        <div className="border-t border-border divide-y divide-border animate-fade-in">
          {needsAttention.length > 0 && (
            <div className="p-3">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Needs attention</p>
              {needsAttention.map(card => <CardRow key={card.id} card={card} onMarkReviewed={onMarkReviewed} onGetPrompt={onGetPrompt} onEdit={onEdit} onOpenReview={onOpenReview} />)}
            </div>
          )}
          {section.upToDate.length > 0 && (
            <div className="p-3">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Up to date ({section.upToDate.length})</p>
              {section.upToDate.map(card => <CardRow key={card.id} card={card} onMarkReviewed={onMarkReviewed} onGetPrompt={onGetPrompt} onEdit={onEdit} onOpenReview={onOpenReview} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CardRow({ card, onMarkReviewed, onGetPrompt, onEdit, onOpenReview }: {
  card: ContentCardFull; onMarkReviewed: (id: string) => void; onGetPrompt: (card: ContentCardFull) => void; onEdit: (card: ContentCardFull) => void;
  onOpenReview?: (card: ContentCardFull) => void;
}) {
  const { daysUntilDue, isOverdue, isDueSoon } = getCardDueInfo(card);
  const dueText = isOverdue ? `${Math.abs(daysUntilDue)} days overdue` : isDueSoon ? `Due in ${daysUntilDue} days` : `Next review in ${daysUntilDue} days`;

  return (
    <div className="flex items-center gap-2 py-2">
      <span className="text-sm shrink-0">{card.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground truncate">{card.title}</p>
        <p className={cn('text-[10px]', isOverdue ? 'text-destructive' : isDueSoon ? 'text-amber-600' : 'text-muted-foreground')}>
          {dueText} · Reviewed {format(parseISO(card.last_reviewed_at), 'd MMM yyyy')}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {(isOverdue || isDueSoon) && (
          <AppIconButton onClick={() => onGetPrompt(card)} title="Get AI prompt">
            <AlertTriangle size={14} className="text-amber-500" />
          </AppIconButton>
        )}
        <AppIconButton onClick={() => onOpenReview?.(card)} title="Review notes">
          <MessageSquare size={14} className="text-muted-foreground" />
        </AppIconButton>
        <AppIconButton onClick={() => onEdit(card)} title="Edit"><Pencil size={14} className="text-muted-foreground" /></AppIconButton>
        <AppIconButton onClick={() => onMarkReviewed(card.id)} title="Mark reviewed"><Check size={14} className="text-primary" /></AppIconButton>
      </div>
    </div>
  );
}

// === Prompt dialogs ===

function generatePrompt(card: ContentCardFull): string {
  const currentYear = new Date().getFullYear();
  const source = card.link_url || 'relevant gov.uk page';
  return `I run a UK consumer app called Adulting (adultingapp.uk) that helps people manage life admin. I need to verify this content is still accurate for ${currentYear}:\n\nCard: ${card.title}\nCurrent content: "${card.summary} ${card.body}"\n\nPlease:\n1. Confirm if this is still accurate, or tell me what has changed\n2. Note any related changes I should know about\n3. Give me the updated card text in exactly the same format, ready to paste back into my app\n\nKey source to check: ${source}`;
}

function generateBulkPrompt(cards: ContentCardFull[]): string {
  const currentYear = new Date().getFullYear();
  const bySection = new Map<string, ContentCardFull[]>();
  for (const c of cards) { if (!bySection.has(c.category)) bySection.set(c.category, []); bySection.get(c.category)!.push(c); }
  let cardEntries = ''; let idx = 1;
  for (const [section, sectionCards] of bySection) { cardEntries += `\n**${section}**\n`; for (const c of sectionCards) { cardEntries += `${idx}. ${c.title}\n   Current: "${c.summary} ${c.body}"\n   Source: ${c.link_url || 'gov.uk'}\n\n`; idx++; } }
  return `I run a UK consumer app called Adulting (adultingapp.uk) that helps people manage life admin. I need to verify the following ${cards.length} pieces of content are still accurate for ${currentYear}:\n${cardEntries}\nFor each card, please:\n1. Confirm if this is still accurate, or tell me what has changed\n2. Note any related changes I should know about\n3. Give me the updated card text in exactly the same format, ready to paste back into my app`;
}

function PromptDialog({ card, onClose }: { card: ContentCardFull | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  if (!card) return null;
  const prompt = generatePrompt(card);
  const handleCopy = () => { navigator.clipboard.writeText(prompt); setCopied(true); toast.success('Prompt copied to clipboard'); setTimeout(() => setCopied(false), 2000); };
  return (
    <Dialog open={!!card} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-base">AI Update Prompt — {card.title}</DialogTitle></DialogHeader>
        <div className="bg-muted rounded-lg p-4 text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed">{prompt}</div>
        <AppButton onPress={handleCopy} width="100%" justifyContent="center">
          {copied ? <ClipboardCheck size={16} /> : <Copy size={16} />} {copied ? 'Copied!' : 'Copy prompt'}
        </AppButton>
        <p className="text-[10px] text-muted-foreground text-center">Paste into Claude or ChatGPT, bring back the updated text, then use the Edit button to save it.</p>
      </DialogContent>
    </Dialog>
  );
}

function BulkPromptDialog({ cards, open, onClose }: { cards: ContentCardFull[]; open: boolean; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  if (!open || cards.length === 0) return null;
  const prompt = generateBulkPrompt(cards);
  const handleCopy = () => { navigator.clipboard.writeText(prompt); setCopied(true); toast.success('Prompt copied to clipboard'); setTimeout(() => setCopied(false), 2000); };
  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-base">Review All Due — AI Prompt ({cards.length} cards)</DialogTitle></DialogHeader>
        <div className="bg-muted rounded-lg p-4 text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed max-h-[40vh] overflow-y-auto">{prompt}</div>
        <AppButton onPress={handleCopy} width="100%" justifyContent="center">
          {copied ? <ClipboardCheck size={16} /> : <Copy size={16} />} {copied ? 'Copied!' : 'Copy prompt'}
        </AppButton>
        <p className="text-[10px] text-muted-foreground text-center">Paste this into ChatGPT or Claude to review all due cards in one session.</p>
      </DialogContent>
    </Dialog>
  );
}

function EditCardDialog({ card, onClose, onSaved }: { card: ContentCardFull; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(card.title);
  const [summary, setSummary] = useState(card.summary);
  const [body, setBody] = useState(card.body);
  const [linkLabel, setLinkLabel] = useState(card.link_label || '');
  const [linkUrl, setLinkUrl] = useState(card.link_url || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await supabase.from('content_cards').update({ title: title.trim(), summary: summary.trim(), body: body.trim(), link_label: linkLabel.trim() || null, link_url: linkUrl.trim() || null, updated_at: new Date().toISOString() } as any).eq('id', card.id);
    await supabase.from('content_review_entries' as any).insert({
      content_type: 'content_card',
      content_id: card.id,
      note: 'Card copy updated in admin.',
      review_outcome: 'updated',
    });
    setSaving(false); toast.success('Card updated'); onSaved();
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-base">Edit — {card.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Title"><AppWebInput type="text" value={title} onChange={e => setTitle(e.target.value)} /></Field>
          <Field label="Summary"><AppWebInput type="text" value={summary} onChange={e => setSummary(e.target.value)} /></Field>
          <Field label="Body"><AppTextArea value={body} onChange={e => setBody(e.target.value)} rows={5} className="bg-background" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Link label"><AppWebInput type="text" value={linkLabel} onChange={e => setLinkLabel(e.target.value)} placeholder="e.g. gov.uk" /></Field>
            <Field label="Link URL"><AppWebInput type="text" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://..." /></Field>
          </div>
          <AppButton onPress={handleSave} disabled={!title.trim() || !body.trim() || saving} width="100%">{saving ? 'Saving…' : 'Save changes'}</AppButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// === Playbook-specific dialogs ===

function PlaybookPromptDialog({ playbook, onClose }: { playbook: Playbook; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const currentYear = new Date().getFullYear();
  const steps = playbook.steps as any[];
  const stepsSummary = steps.map((s: any) => `  Step ${s.step}: ${s.title} — ${s.description}`).join('\n');

  const prompt = `I run a UK consumer app called Adulting (adultingapp.uk) that helps people manage life admin. I need to verify this playbook is still accurate for ${currentYear}:\n\nPlaybook: ${playbook.title}\nSection: ${playbook.section}\nDescription: ${playbook.description}\n\nCurrent steps:\n${stepsSummary}\n\nPlease:\n1. Confirm if each step is still accurate, or tell me what has changed\n2. Note any new steps that should be added or existing ones that should be removed\n3. Check any links or figures mentioned are still current\n4. Give me the updated steps in the same JSON format, ready to paste back into my app`;

  const handleCopy = () => { navigator.clipboard.writeText(prompt); setCopied(true); toast.success('Prompt copied'); setTimeout(() => setCopied(false), 2000); };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-base">AI Prompt — {playbook.title}</DialogTitle></DialogHeader>
        <div className="bg-muted rounded-lg p-4 text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed max-h-[40vh] overflow-y-auto">{prompt}</div>
        <AppButton onPress={handleCopy} width="100%" justifyContent="center">
          {copied ? <ClipboardCheck size={16} /> : <Copy size={16} />} {copied ? 'Copied!' : 'Copy prompt'}
        </AppButton>
      </DialogContent>
    </Dialog>
  );
}

function EditPlaybookDialog({ playbook, onClose, onSaved }: { playbook: Playbook | null; onClose: () => void; onSaved: () => void }) {
  const isNew = !playbook;
  const [title, setTitle] = useState(playbook?.title || '');
  const [description, setDescription] = useState(playbook?.description || '');
  const [section, setSection] = useState(playbook?.section || 'home');
  const [icon, setIcon] = useState(playbook?.icon || '📖');
  const [difficulty, setDifficulty] = useState(playbook?.difficulty || 'moderate');
  const [estimatedTime, setEstimatedTime] = useState(playbook?.estimated_time || '');
  const [sortOrder, setSortOrder] = useState(playbook?.sort_order ?? 0);
  const [isActive, setIsActive] = useState(playbook?.is_active ?? true);
  const [stepsJson, setStepsJson] = useState(() => JSON.stringify(playbook?.steps || [], null, 2));
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState('');

  const handleSave = async () => {
    let parsedSteps;
    try { parsedSteps = JSON.parse(stepsJson); setJsonError(''); } catch { setJsonError('Invalid JSON'); return; }

    setSaving(true);
    if (isNew) {
      await supabase.from('playbooks').insert({
        title: title.trim(), description: description.trim(), section, icon, difficulty, estimated_time: estimatedTime.trim() || null,
        sort_order: sortOrder, is_active: isActive, steps: parsedSteps,
      } as any);
    } else {
      await supabase.from('playbooks').update({
        title: title.trim(), description: description.trim(), section, icon, difficulty, estimated_time: estimatedTime.trim() || null,
        sort_order: sortOrder, is_active: isActive, steps: parsedSteps, updated_at: new Date().toISOString(),
      } as any).eq('id', playbook!.id);
      await supabase.from('content_review_entries' as any).insert({
        content_type: 'playbook',
        content_id: playbook!.id,
        note: 'Guide content updated in admin.',
        review_outcome: 'updated',
      });
    }
    setSaving(false); toast.success(isNew ? 'Guide created' : 'Guide updated'); onSaved();
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-base">{isNew ? 'Add Guide' : `Edit — ${playbook!.title}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Field label="Title"><AppWebInput type="text" value={title} onChange={e => setTitle(e.target.value)} /></Field>
            <Field label="Icon"><AppWebInput type="text" value={icon} onChange={e => setIcon(e.target.value)} className="w-16 text-center" /></Field>
          </div>
          <Field label="Description"><AppWebInput type="text" value={description} onChange={e => setDescription(e.target.value)} /></Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Section">
              <AppNativeSelect value={section} onChange={e => setSection(e.target.value)}>
                <option value="home">Home</option><option value="work">Work</option><option value="health">Health</option>
              </AppNativeSelect>
            </Field>
            <Field label="Difficulty">
              <AppNativeSelect value={difficulty} onChange={e => setDifficulty(e.target.value)}>
                <option value="straightforward">Straightforward</option><option value="moderate">Moderate</option><option value="complex">Complex</option>
              </AppNativeSelect>
            </Field>
            <Field label="Est. time"><AppWebInput type="text" value={estimatedTime} onChange={e => setEstimatedTime(e.target.value)} placeholder="e.g. 2 weeks" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Sort order"><AppWebInput type="number" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))} /></Field>
            <Field label="Active">
              <label className="flex items-center gap-2 mt-1.5 cursor-pointer">
                <AppNativeCheckbox checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                <span className="text-sm text-foreground">{isActive ? 'Yes' : 'No'}</span>
              </label>
            </Field>
          </div>
          <Field label="Steps (JSON)">
            <AppTextArea value={stepsJson} onChange={e => { setStepsJson(e.target.value); setJsonError(''); }} rows={10} className="bg-background text-xs font-mono" />
            {jsonError && <p className="text-[10px] text-destructive mt-1">{jsonError}</p>}
          </Field>
          <AppButton onPress={handleSave} disabled={!title.trim() || !description.trim() || saving} width="100%">
            {saving ? 'Saving…' : isNew ? 'Create guide' : 'Save changes'}
          </AppButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReviewHistoryDialog({
  target,
  entries,
  loading,
  draft,
  saving,
  onDraftChange,
  onSave,
  onClose,
}: {
  target: ReviewTarget;
  entries: ContentReviewEntry[];
  loading: boolean;
  draft: string;
  saving: boolean;
  onDraftChange: (value: string) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-base">Review history — {target.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-background px-4 py-3 space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Add note</p>
            <AppTextArea
              value={draft}
              onChange={(event) => onDraftChange(event.target.value)}
              rows={3}
              maxLength={600}
              placeholder="Capture what changed, what you checked, or what still needs follow-up."
              className="bg-card"
            />
            <AppButton onPress={onSave} disabled={!draft.trim() || saving} width="100%">
              {saving ? 'Saving note…' : 'Save review note'}
            </AppButton>
          </div>

          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">History</p>
            {loading ? (
              <div className="rounded-xl border border-border bg-background px-4 py-4">
                <p className="text-sm text-muted-foreground">Loading review history…</p>
              </div>
            ) : entries.length === 0 ? (
              <div className="rounded-xl border border-border bg-background px-4 py-4">
                <p className="text-sm font-medium text-foreground">No review notes yet.</p>
                <p className="text-xs text-muted-foreground mt-1">Use this log to keep source checks, updates, and follow-up decisions in one place.</p>
              </div>
            ) : (
              entries.map((entry) => (
                <div key={entry.id} className="rounded-xl border border-border bg-background px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                      {entry.review_outcome === 'reviewed' ? 'Reviewed' : entry.review_outcome === 'updated' ? 'Updated' : 'Note'}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {format(new Date(entry.created_at), 'd MMM yyyy, HH:mm')}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-foreground whitespace-pre-wrap leading-relaxed">{entry.note}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// === Shared ===
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>{children}</div>;
}
